import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { useToast } from "@/hooks/use-toast";

export const useProfile = () => {
    const { user, signOut } = useAuth();
    const navigate = useNavigate();
    const { toast } = useToast();

    const [loading, setLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    const [profile, setProfile] = useState({
        email: user?.email || '',
        display_name: user?.user_metadata?.full_name || '',
        avatar_url: user?.user_metadata?.avatar_url || '',
        company: '',
        total_websites_analyzed: 0,
        total_matches_found: 0,
        total_custom_types_created: 0,
        total_active_custom_types: 0,
        credits: 0,
        created_at: new Date().toISOString()
    });

    const [editForm, setEditForm] = useState({
        display_name: '',
        company: ''
    });

    useEffect(() => {
        fetchProfile();
    }, [user]);

    const fetchProfile = async () => {
        try {
            const { data, error } = await supabase
                .from('profiles')
                .select('*')
                .eq('id', user.id)
                .single();

            if (error) throw error;

            setProfile({
                ...profile,
                ...data,
                // Keep auth data from user object
                email: user.email,
                display_name: user.user_metadata?.full_name || '',
                avatar_url: user.user_metadata?.avatar_url || ''
            });

            setEditForm({
                display_name: user.user_metadata?.full_name || '',
                company: data.company || ''
            });
        } catch (error) {
            toast({
                variant: "destructive",
                title: "Error",
                description: "Failed to load profile data. Please try again later."
            });
            console.error('Error fetching profile:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleEditToggle = () => {
        if (isEditing) {
            setEditForm({
                display_name: profile.display_name,
                company: profile.company
            });
        }
        setIsEditing(!isEditing);
    };

    const updateProfile = async () => {
        try {
            // First update user metadata (name)
            const { data: userData, error: userError } = await supabase.auth.updateUser({
                data: {
                    full_name: editForm.display_name
                }
            });

            if (userError) throw userError;

            // Then update profile data (company)
            const { data: profileData, error: profileError } = await supabase
                .from('profiles')
                .update({ company: editForm.company })
                .eq('id', user.id)
                .select()
                .single();

            if (profileError) throw profileError;

            // Update local state with new data
            setProfile(prev => ({
                ...prev,
                display_name: editForm.display_name,
                company: editForm.company
            }));

            setIsEditing(false);
            toast({
                title: "Success",
                description: "Profile updated successfully"
            });
        } catch (error) {
            console.error('Error updating profile:', error);
            toast({
                variant: "destructive",
                title: "Error",
                description: error.message || "Failed to update profile. Please try again later."
            });
        }
    };

    const deleteAccount = async () => {
        try {
            // First, delete the profile
            const { error: profileError } = await supabase
                .from('profiles')
                .delete()
                .eq('id', user.id);

            if (profileError) throw profileError;

            // Then, delete the user authentication data
            const { error: authError } = await supabase.auth.admin.deleteUser(user.id);

            if (authError) throw authError;

            // Sign out the user
            await signOut();

            // Redirect to login page
            navigate('/login');

            toast({
                title: "Account Deleted",
                description: "Your account has been successfully deleted"
            });
        } catch (error) {
            toast({
                variant: "destructive",
                title: "Error",
                description: "Failed to delete account. Please try again later."
            });
            console.error('Error deleting account:', error);
            setShowDeleteConfirm(false);
        }
    };

    const handleInputChange = (e) => {
        setEditForm(prev => ({
            ...prev,
            [e.target.name]: e.target.value
        }));
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    return {
        profile,
        loading,
        isEditing,
        editForm,
        showDeleteConfirm,
        handleEditToggle,
        updateProfile,
        deleteAccount,
        handleInputChange,
        formatDate,
        setShowDeleteConfirm
    };
};